import { BackendClient } from '~/backend/backend-client';
import { PollData, PollingStatus, ResultID } from '~/backend/types';
import { JobPayload } from '~/state/atoms/jobAtoms';

export type UploadSuccessSummary = {
    jobId: string;
    totalFiles: number;
    submittedFiles: number;
};

/**
 * Uploads files to the backend for a given job.
 * It handles multiple files concurrently and reports progress.
 * @param jobId - The unique identifier for the job.
 * @param client - The `BackendClient` instance used for file submission.
 * @param payload - The job payload, containing files to upload and any backend-specific parameters.
 * @param onProgressUpdate - A callback function that is invoked with the count of successfully uploaded files as progress is made.
 * @returns A promise that resolves to an `UploadSuccessSummary` object, detailing the total and submitted file counts.
 * @throws Throws an error if no files are successfully uploaded or if the operation is cancelled.
 */
export async function uploadFiles(
    jobId: string,
    client: BackendClient,
    payload: JobPayload,
    onProgressUpdate: (successfullySubmittedCount: number) => void
): Promise<UploadSuccessSummary> {
    let successfullySubmittedCount = 0;
    const totalFilesToUpload = payload.filesToUpload.length;

    if (totalFilesToUpload === 0) {
        return {
            jobId,
            totalFiles: 0,
            submittedFiles: 0,
        };
    }

    try {
        await Promise.all(
            payload.filesToUpload.map(async (blobWithName) => {
                const fileSubmittedSuccessfully = await client.submitFile(
                    blobWithName,
                    payload.backendParameters
                );

                if (fileSubmittedSuccessfully) {
                    successfullySubmittedCount++;
                    onProgressUpdate(successfullySubmittedCount);
                } else {
                    console.warn(
                        `Job ${jobId}: Failed uploading file "${blobWithName}"`
                    );
                }
            })
        );

        if (totalFilesToUpload > 0 && successfullySubmittedCount === 0) {
            throw new Error(
                `Job ${jobId}: Upload failed. No files were successfully submitted out of ${totalFilesToUpload}.`
            );
        }

        return {
            jobId,
            totalFiles: totalFilesToUpload,
            submittedFiles: successfullySubmittedCount,
        };
    } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
            throw new Error(`Uploading cancelled`);
        }
        throw new Error(`Uploading files failed for job ${jobId}`);
    }
}

const DEFAULT_POLLING_INTERVAL = 1000;

/**
 * Periodically polls the backend for job results until they are available.
 * It continues polling at a specified interval until the backend signals that
 * results are ready or if the operation is cancelled.
 * @param jobId - The unique identifier for the job, used for error reporting.
 * @param client - The `BackendClient` instance to use for polling.
 * @param intervalMs - The time in milliseconds between polling attempts.
 * @returns A promise that resolves with `PollData` when the job is complete.
 * @throws Rejects the promise if polling is cancelled or fails.
 */
export async function pollForResults(
    jobId: string,
    client: BackendClient,
    intervalMs: number = DEFAULT_POLLING_INTERVAL
): Promise<PollData> {
    return new Promise((resolve, reject) => {
        const poll = async () => {
            if (client.signal?.aborted) {
                return reject(new DOMException('Task cancelled', 'AbortError'));
            }
            try {
                const response = await client.poll();
                if (response.status === PollingStatus.HAS_RESULT) {
                    resolve(response);
                } else {
                    setTimeout(poll, intervalMs);
                }
            } catch (error) {
                reject(new Error(`Job ${jobId}: Polling failed.`));
            }
        };
        void poll();
    });
}

export type DownloadedResults = {
    files: File[];
    errors: string[];
    downloadErrors: string[];
};

/**
 * Downloads files associated with a result ID and handles any errors that occur during the download.
 * @param client - The `BackendClient` instance used for downloading files.
 * @param resultItem - The result ID containing file paths to download.
 * @returns An object containing the downloaded files and any errors encountered during the process.
 */
async function downloadResultItem(
    client: BackendClient,
    resultItem: ResultID
): Promise<{ files: File[]; errors: string[] }> {
    const downloadedFiles: File[] = [];
    const itemProcessingErrors: string[] = [];

    if (!resultItem.file_ids || resultItem.file_ids.length === 0) {
        return { files: [], errors: [] };
    }

    try {
        const filesFromResultItem: (File | undefined)[] = await Promise.all(
            resultItem.file_ids.map(async (filePath) => {
                try {
                    const blob = await client.downloadFile(
                        resultItem.id,
                        filePath
                    );
                    if (blob) {
                        return new File([blob], filePath || resultItem.id);
                    } else {
                        itemProcessingErrors.push(
                            `No blob for result ${resultItem.id}, path ${filePath}`
                        );
                    }
                } catch (downloadError) {
                    itemProcessingErrors.push(
                        `Failed to download file ${filePath} for result ID ${resultItem.id}.`
                    );
                    return undefined;
                }
            })
        );
        downloadedFiles.push(
            ...filesFromResultItem.filter(
                (file): file is File => file !== undefined
            )
        );
    } catch (error) {
        const errMsg = `Unexpected error processing files for result ID ${resultItem.id}.`;
        itemProcessingErrors.push(errMsg);
    }

    // After all files downloaded for a result ID, try to delete it.
    client.deleteResult(resultItem.id);
    return { files: downloadedFiles, errors: itemProcessingErrors };
}

/**
 * Fetches error information from the backend for a given error ID.
 * After fetching the error, it attempts to delete the corresponding result from the backend.
 * @param client The `BackendClient` instance for API calls.
 * @param errorItem The `ResultID` object that represents a backend error.
 * @returns A promise that resolves to a string containing the error details.
 */
async function downloadErroritem(
    client: BackendClient,
    errorItem: ResultID
): Promise<string | null> {
    try {
        const errorDetails = await client.getErrors(errorItem.id);
        if (errorDetails) {
            return typeof errorDetails === 'string'
                ? errorDetails
                : JSON.stringify(errorDetails);
        }
        client.deleteResult(errorItem.id);
        return `No error details for ID ${errorItem.id}`;
    } catch (getErrorError) {
        return `Failed to retrieve error details for ID ${errorItem.id}`;
    }
}

/**
 * Downloads all results and fetches all error details for a completed job.
 * It processes both successful results and error reports from the backend.
 * @param jobId - The unique identifier for the job, used for logging and error reporting.
 * @param client - The `BackendClient` instance used for downloading files and fetching error details.
 * @param pollData - The data from the polling response, containing arrays of result and error IDs.
 * @returns A promise that resolves to a `DownloadedResults` object, which aggregates all downloaded files, backend error messages, and any errors that occurred during the download process.
 */
export async function downloadBackendResults(
    jobId: string,
    client: BackendClient,
    pollData: PollData
): Promise<DownloadedResults> {
    const allDownloadedFiles: File[] = [];
    const allBackendErrors: string[] = [];
    const allDownloadAndProcessingErrors: string[] = [];

    if (
        pollData.result?.result_ids_v2 &&
        pollData.result?.result_ids_v2.length > 0
    ) {
        const resultOutcomes = await Promise.allSettled(
            pollData.result.result_ids_v2.map((resultItem) =>
                downloadResultItem(client, resultItem)
            )
        );

        resultOutcomes.forEach((outcome) => {
            if (outcome.status === 'fulfilled') {
                allDownloadedFiles.push(...outcome.value.files);
                allDownloadAndProcessingErrors.push(...outcome.value.errors);
            } else {
                allDownloadAndProcessingErrors.push(
                    `Error processing a result item for job ${jobId}. Error: ${outcome.reason?.message || outcome.reason}`
                );
            }
        });
    }

    if (
        pollData.result?.error_ids_v2 &&
        pollData.result.error_ids_v2.length > 0
    ) {
        const errorOutcomes = await Promise.allSettled(
            pollData.result.error_ids_v2.map((errorItem) =>
                downloadErroritem(client, errorItem)
            )
        );

        errorOutcomes.forEach((outcome) => {
            if (outcome.status === 'fulfilled') {
                if (outcome.value) {
                    allBackendErrors.push(outcome.value);
                }
            } else {
                allDownloadAndProcessingErrors.push(
                    `Error processing an error item for job ${jobId}. Error: ${outcome.reason?.message || outcome.reason}`
                );
            }
        });
    }

    return {
        files: allDownloadedFiles,
        errors: allBackendErrors,
        downloadErrors: allDownloadAndProcessingErrors,
    };
}
